import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PermissionEntry {
  module: string;
  action: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private static instance: PermissionsService | null = null;

  /** Fallback estático (misma matriz legacy) si aún no cargó el API. */
  private staticFallback: Record<string, string[]> = {
    dashboard: ['ADMINISTRADOR', 'VENDEDOR', 'PRODUCCION', 'MASTER'],
    users: ['ADMINISTRADOR', 'MASTER'],
    clients: ['ADMINISTRADOR', 'VENDEDOR', 'MASTER'],
    products: ['ADMINISTRADOR', 'MASTER'],
    inventory: ['ADMINISTRADOR', 'MASTER', 'VENDEDOR', 'PRODUCCION'],
    expenses: ['ADMINISTRADOR', 'MASTER'],
    suppliers: ['ADMINISTRADOR', 'MASTER'],
    payables: ['ADMINISTRADOR', 'MASTER'],
    treasury: ['ADMINISTRADOR', 'MASTER'],
    'cash-flow': ['ADMINISTRADOR', 'MASTER'],
    warehouses: ['ADMINISTRADOR', 'MASTER'],
    quotes: ['ADMINISTRADOR', 'VENDEDOR', 'MASTER'],
    orders: ['ADMINISTRADOR', 'VENDEDOR', 'MASTER'],
    invoices: ['ADMINISTRADOR', 'VENDEDOR', 'MASTER'],
    'purchase-requests': ['ADMINISTRADOR', 'VENDEDOR', 'PRODUCCION', 'MASTER'],
    reports: ['ADMINISTRADOR', 'MASTER'],
    kanban: ['ADMINISTRADOR', 'PRODUCCION', 'MASTER', 'VENDEDOR'],
    calendar: ['ADMINISTRADOR', 'MASTER', 'VENDEDOR'],
    ecommerce_products: ['MASTER'],
    ecommerce_colors: ['MASTER'],
    ecommerce_sizes: ['MASTER'],
    ecommerce_categories: ['MASTER'],
    ecommerce_brands: ['MASTER'],
    logs: ['MASTER'],
    'production-orders': ['ADMINISTRADOR', 'VENDEDOR', 'PRODUCCION', 'MASTER'],
    profiles: ['ADMINISTRADOR', 'MASTER'],
  };

  private loadedPermissions: PermissionEntry[] | null = null;
  private loadPromise: Promise<void> | null = null;

  constructor(private http: HttpClient) {
    PermissionsService.instance = this;
  }

  static getInstance(): PermissionsService | null {
    return PermissionsService.instance;
  }

  getCurrentRole(): string {
    return localStorage.getItem('rol') || '';
  }

  isAdminRole(role?: string): boolean {
    const current = role || this.getCurrentRole();
    return current === 'ADMINISTRADOR' || current === 'MASTER';
  }

  /** Carga permisos del perfil desde API (llamar tras login / app init). */
  async loadFromApi(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = (async () => {
      try {
        const res = await firstValueFrom(
          this.http.get<{
            permissions: PermissionEntry[];
            profileCode?: string | null;
          }>(`${environment.url_api}/profiles/me/permissions`),
        );
        this.loadedPermissions = res.permissions || [];
        if (res.profileCode) {
          localStorage.setItem('profileCode', res.profileCode);
        }
      } catch {
        this.loadedPermissions = null;
      } finally {
        this.loadPromise = null;
      }
    })();
    return this.loadPromise;
  }

  clearCache(): void {
    this.loadedPermissions = null;
  }

  hasPermission(permissionName: string, action: string = 'view'): boolean {
    const currentRole = this.getCurrentRole();
    if (currentRole === 'MASTER') return true;

    if (this.loadedPermissions) {
      return this.loadedPermissions.some(
        (p) => p.module === permissionName && p.action === action,
      );
    }

    const roles = this.staticFallback[permissionName];
    if (!roles) return false;
    return roles.includes(currentRole);
  }

  hasAnyPermission(permissionNames: string[]): boolean {
    return permissionNames.some((permission) => this.hasPermission(permission));
  }

  canChangeStatus(module: string): boolean {
    return this.hasPermission(module, 'change_status');
  }

  getProductionAreas(): string[] {
    if (!this.loadedPermissions) return ['BORDADO', 'SUBLIMADO'];
    return this.loadedPermissions
      .filter(
        (p) =>
          p.module === 'production-orders' && p.action.startsWith('area:'),
      )
      .map((p) => p.action.replace('area:', ''));
  }
}
